(function () {
  const CARD_SELECTOR = '[data-card-color-swatch-card]';
  const ROW_SELECTOR = '[data-card-color-swatches]';
  const LIST_SELECTOR = '[data-card-color-swatches-list]';
  const SWATCH_SELECTOR = '[data-card-color-swatch]';
  const OVERFLOW_SELECTOR = '[data-card-swatch-overflow]';
  const OVERFLOW_COUNT_SELECTOR = '[data-card-swatch-overflow-count]';
  const SOLD_OUT_JSON_SELECTOR = '[data-card-sold-out-count-json]';
  const LOCALIZED_SWATCH_SELECTOR = '[data-localized-swatch-image]';
  const localizedProductCache = new Map();

  const HOVER_PREVIEW_DELAY = 65;
  const HOVER_LEAVE_DELAY = 90;
  const MAX_VISIBLE_SWATCHES = 3;

  const cardTimers = new WeakMap();

  function getTimers(card) {
    if (!cardTimers.has(card)) {
      cardTimers.set(card, {
        previewTimer: null,
        leaveTimer: null,
      });
    }

    return cardTimers.get(card);
  }

  function clearCardTimers(card) {
    const timers = getTimers(card);
    window.clearTimeout(timers.previewTimer);
    window.clearTimeout(timers.leaveTimer);
    timers.previewTimer = null;
    timers.leaveTimer = null;
  }

  function getHiddenOverflowCount(row) {
    const shouldCountHiddenColors =
      row.getAttribute('data-card-count-hidden-colors') === 'true';

    if (!shouldCountHiddenColors) return 0;

    const card = row.closest(CARD_SELECTOR);
    const fallback = Number.parseInt(row.getAttribute('data-sold-out-count') || '0', 10) || 0;

    if (!card) return fallback;

    const json = card.querySelector(SOLD_OUT_JSON_SELECTOR);
    if (!json) return fallback;

    try {
      const parsed = JSON.parse(json.textContent || '{}');
      return Number.parseInt(parsed.soldOutCount || fallback, 10) || 0;
    } catch (_error) {
      return fallback;
    }
  }

  function setOverflow(row) {
    const list = row.querySelector(LIST_SELECTOR);
    const overflow = row.querySelector(OVERFLOW_SELECTOR);
    const overflowCount = row.querySelector(OVERFLOW_COUNT_SELECTOR);
    const swatches = Array.from(row.querySelectorAll(SWATCH_SELECTOR));

    if (!list || !overflow || !overflowCount || swatches.length === 0) {
      if (overflow) overflow.hidden = true;
      return;
    }

    const hiddenOverflowCount = getHiddenOverflowCount(row);

    swatches.forEach((swatch) => {
      swatch.hidden = false;
    });

    overflow.hidden = true;
    overflow.style.visibility = '';

    const gap = getGap(list);
    const rowWidth = row.clientWidth;

    const renderedSwatchesNeedOverflow = swatches.length > MAX_VISIBLE_SWATCHES;

    let totalSwatchWidth = 0;
    swatches.forEach((swatch) => {
      totalSwatchWidth += swatch.offsetWidth;
    });

    totalSwatchWidth += gap * Math.max(swatches.length - 1, 0);

    const widthNeedsOverflow = rowWidth > 0 && totalSwatchWidth > rowWidth;
    const needsOverflow = hiddenOverflowCount > 0 || renderedSwatchesNeedOverflow || widthNeedsOverflow;

    let availableWidth = rowWidth;

    if (needsOverflow) {
      overflow.hidden = false;
      overflow.style.visibility = 'hidden';
      overflowCount.textContent = hiddenOverflowCount || 0;

      if (rowWidth > 0) {
        availableWidth = rowWidth - overflow.offsetWidth - gap;
      }
    }

    let usedWidth = 0;
    let hiddenRenderedCount = 0;
    let shownCount = 0;

    swatches.forEach((swatch, index) => {
      const swatchWidth = swatch.offsetWidth;
      const nextWidth = usedWidth + swatchWidth + (index > 0 ? gap : 0);

      const exceedsMaxVisible = shownCount >= MAX_VISIBLE_SWATCHES;
      const exceedsAvailableWidth = needsOverflow && rowWidth > 0 && nextWidth > availableWidth;

      if (exceedsMaxVisible || exceedsAvailableWidth) {
        swatch.hidden = true;
        hiddenRenderedCount += 1;
      } else {
        usedWidth = nextWidth;
        shownCount += 1;
      }
    });

    const totalHiddenCount = hiddenRenderedCount + hiddenOverflowCount;

    if (totalHiddenCount > 0) {
      overflowCount.textContent = totalHiddenCount;
      overflow.hidden = false;
      overflow.style.visibility = '';
    } else {
      overflow.hidden = true;
      overflow.style.visibility = '';
    }
  }

  function getGap(element) {
    const styles = window.getComputedStyle(element);
    const columnGap = Number.parseFloat(styles.columnGap || styles.gap || '0');
    return Number.isNaN(columnGap) ? 0 : columnGap;
  }

  function updateCardImage(card, swatch) {
    const variantImage = swatch.getAttribute('data-variant-image');
    const variantImageAlt = swatch.getAttribute('data-variant-image-alt');
    const variantImageAspect = swatch.getAttribute('data-variant-image-aspect');

    if (!variantImage) return;

    const image = card.querySelector('[data-card-primary-image]');
    if (!image) return;

    image.setAttribute('src', variantImage);
    image.setAttribute('data-src', variantImage);

    if (variantImageAlt) {
      image.setAttribute('alt', variantImageAlt);
    }

    if (variantImageAspect) {
      image.setAttribute('data-aspect', variantImageAspect);
    }
  }

  function updateCardTitle(card, swatch) {
    const variantTitle = swatch.getAttribute('data-variant-title');
    if (!variantTitle) return;

    const title = card.querySelector('[data-card-color-title]');
    if (title) {
      title.textContent = variantTitle;
    }
  }

  function updateCardLinks(card, swatch) {
    const variantUrl = swatch.getAttribute('data-variant-url');
    if (!variantUrl) return;

    card.querySelectorAll('[data-card-dynamic-link]').forEach((link) => {
      link.setAttribute('href', variantUrl);
    });
  }

  function setActiveSwatch(card, swatch) {
    card.querySelectorAll(SWATCH_SELECTOR).forEach((item) => {
      item.classList.toggle('is-active', item === swatch);
    });
  }

  function previewCardFromSwatch(swatch) {
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    card.classList.add('is-swatch-hovering');
    card.classList.add('is-swatch-selected');

    updateCardImage(card, swatch);
    updateCardTitle(card, swatch);
  }

  function commitCardFromSwatch(swatch) {
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    const variantId = swatch.getAttribute('data-variant-id');

    clearCardTimers(card);

    card.classList.add('is-swatch-hovering');
    card.classList.add('is-swatch-selected');

    if (variantId) {
      card.setAttribute('data-variant-id', variantId);
    }

    updateCardImage(card, swatch);
    updateCardTitle(card, swatch);
    updateCardLinks(card, swatch);
    setActiveSwatch(card, swatch);
  }

  function restoreCommittedCard(card) {
    const activeSwatch = card.querySelector(`${SWATCH_SELECTOR}.is-active`);

    if (activeSwatch) {
      updateCardImage(card, activeSwatch);
      updateCardTitle(card, activeSwatch);
      updateCardLinks(card, activeSwatch);
    }

    card.classList.remove('is-swatch-hovering');
  }

  function scheduleHoverPreview(swatch) {
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    const timers = getTimers(card);

    window.clearTimeout(timers.previewTimer);
    window.clearTimeout(timers.leaveTimer);

    card.classList.add('is-swatch-hovering');
    card.classList.add('is-swatch-selected');

    timers.previewTimer = window.setTimeout(function () {
      previewCardFromSwatch(swatch);
    }, HOVER_PREVIEW_DELAY);
  }

  function scheduleHoverLeave(swatch, relatedTarget) {
    const card = swatch.closest(CARD_SELECTOR);
    if (!card) return;

    const row = swatch.closest(ROW_SELECTOR);

    if (relatedTarget && row && row.contains(relatedTarget)) {
      return;
    }

    const timers = getTimers(card);

    window.clearTimeout(timers.previewTimer);
    window.clearTimeout(timers.leaveTimer);

    timers.leaveTimer = window.setTimeout(function () {
      restoreCommittedCard(card);
    }, HOVER_LEAVE_DELAY);
  }

  function handleize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function isFrenchPage() {
    return window.location.pathname === '/fr' || window.location.pathname.indexOf('/fr/') === 0;
  }

  function getSwatchTemplateUrl(element) {
    return element.getAttribute('data-swatch-image-template') || '';
  }

  function getSwatchVariantId(element) {
    const directVariantId = element.getAttribute('data-variant-id');
    if (directVariantId) return directVariantId;

    const swatchButton = element.closest('[data-card-color-swatch]');
    if (!swatchButton) return '';

    return swatchButton.getAttribute('data-variant-id') || '';
  }

  function applyLocalizedSwatchImage(element, productHandle, englishColorValue) {
    const template = getSwatchTemplateUrl(element);
    if (!template || !productHandle || !englishColorValue) return;

    const englishColorHandle = handleize(englishColorValue);
    const swatchFileHandle = `${productHandle}-${englishColorHandle}`;
    const imageUrl = template.replace('__SWATCH_FILE_HANDLE__', swatchFileHandle);

    element.style.backgroundImage = `url(${imageUrl})`;
  }

  function getEnglishProduct(productHandle) {
    if (!productHandle) return Promise.resolve(null);

    if (!localizedProductCache.has(productHandle)) {
      localizedProductCache.set(
        productHandle,
        fetch(`/products/${productHandle}.json?locale=en`)
          .then((response) => {
            if (!response.ok) {
              return fetch(`/products/${productHandle}.json`);
            }

            return response;
          })
          .then((response) => {
            if (!response.ok) throw new Error(`Failed to load product JSON for ${productHandle}`);
            return response.json();
          })
          .then((data) => data.product || null)
          .catch((_error) => null)
      );
    }

    return localizedProductCache.get(productHandle);
  }

  function getEnglishColorOptionIndex(englishProduct) {
    if (!englishProduct || !Array.isArray(englishProduct.options)) return -1;

    for (let index = 0; index < englishProduct.options.length; index += 1) {
      const option = englishProduct.options[index];
      const optionName = typeof option === 'string' ? option : option && option.name;
      const optionHandle = handleize(optionName);

      if (
        optionHandle === 'color' ||
        optionHandle === 'colour' ||
        optionHandle === 'couleur' ||
        optionHandle.indexOf('color') !== -1 ||
        optionHandle.indexOf('colour') !== -1
      ) {
        if (option && typeof option === 'object' && option.position) {
          return Number(option.position) - 1;
        }

        return index;
      }
    }

    return -1;
  }

  function getVariantOptionValue(variant, optionIndex) {
    if (!variant || optionIndex < 0) return '';

    if (Array.isArray(variant.options) && variant.options[optionIndex]) {
      return variant.options[optionIndex];
    }

    return variant[`option${optionIndex + 1}`] || '';
  }

  function getEnglishVariantColorValue(englishProduct, variantId) {
    if (!englishProduct || !Array.isArray(englishProduct.variants) || !variantId) return '';

    const englishVariant = englishProduct.variants.find((variant) => {
      return String(variant.id) === String(variantId);
    });

    if (!englishVariant) return '';

    const colorOptionIndex = getEnglishColorOptionIndex(englishProduct);
    return getVariantOptionValue(englishVariant, colorOptionIndex);
  }

  function updateLocalizedSwatchImages(root) {
    if (!isFrenchPage()) return;

    const scope = root || document;
    const swatches = Array.from(scope.querySelectorAll(LOCALIZED_SWATCH_SELECTOR));

    if (!swatches.length) return;

    const groupedByProduct = swatches.reduce((groups, element) => {
      const productHandle = element.getAttribute('data-product-handle');
      if (!productHandle) return groups;

      if (!groups[productHandle]) groups[productHandle] = [];
      groups[productHandle].push(element);

      return groups;
    }, {});

    Object.keys(groupedByProduct).forEach((productHandle) => {
      getEnglishProduct(productHandle).then((englishProduct) => {
        if (!englishProduct) return;

        groupedByProduct[productHandle].forEach((element) => {
          const variantId = getSwatchVariantId(element);
          const englishColorValue = getEnglishVariantColorValue(englishProduct, variantId);

          if (!englishColorValue) return;

          applyLocalizedSwatchImage(element, productHandle, englishColorValue);
        });
      });
    });
  }

  function initCardSwatches(root) {
    const scope = root || document;

    scope.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      setOverflow(row);
    });

    updateLocalizedSwatchImages(scope);
  }

  function bindEvents() {
    document.addEventListener(
      'click',
      function (event) {
        const swatch = event.target.closest(SWATCH_SELECTOR);
        if (!swatch) return;

        event.preventDefault();
        event.stopPropagation();

        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        commitCardFromSwatch(swatch);
      },
      true
    );

    document.addEventListener('mouseover', function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      scheduleHoverPreview(swatch);
    });

    document.addEventListener('mouseout', function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      scheduleHoverLeave(swatch, event.relatedTarget);
    });

    document.addEventListener('focusin', function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      scheduleHoverPreview(swatch);
    });

    document.addEventListener('focusout', function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);
      if (!swatch) return;

      scheduleHoverLeave(swatch, event.relatedTarget);
    });

    let resizeTimer = null;

    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        initCardSwatches(document);
      }, 150);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initCardSwatches(document);
    bindEvents();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initCardSwatches(event.target || document);
  });
})();